import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';

//MUI
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import Tabs from '@mui/material/Tabs';
import Hidden from '@mui/material/Hidden';
import {styled} from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { plantedSpecies } from '../../../utils/plantedSpecies';

//BIKENTA
import BiomasCO2Menu from './BiomasCO2/BiomasCO2Menu';
import DropDown from '../../DropDown';
import MaderaPlusResults from '../MaderaPlus/MaderaPlusResults';
import MaderaPluspProspectionPointsCalculation from '../MaderaPlus/MaderaPluspProspectionPointsCalculation';
import MenuHeader from './MenuHeader';
import MenuData from './MenuData';
import MenuExistences from './MenuExistences';
import MenuInterventionsList from './MenuInterventionsList';
import DialogPanel from '../../DialogPanel';
import VisitMenu from '../../Visits/VisitMenu';

//UTILS
import {ROLES_LABEL, SHOW_BIOMAS_MODULE, SPECIES_INCLUDED_BIOMASCO2} from '../../../config';
import {prospectingPoints} from '../../proptypes/prospectingPoints';
import {annualPlan} from '../../proptypes/annualPlan';
import {useTranslation} from 'react-i18next';
import {visit} from '../../proptypes/visit';
import {projectSummary} from '../../proptypes/projectSummary';
import {interventionZone} from '../../proptypes/interventionZone';
import {existences} from '../../proptypes/existences';
import {hasViewPermissions} from '../../../modules/useCases';
import {VIEW_PERMISSIONS} from '../../../config';

//STYLES
const TabContent = styled(TabPanel)({
  padding: '8px 0 0 4px',
  marginTop: 0
});
const layerTab = {
  minWidth: 60,
  width: 'auto',
  margin: '0px 12px 0px 0px',
  bottom: -5,
  p: 0
};
const tabIntervention = {
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '36vh',
};

const InterventionZoneMenu = ({
  interventionZone,
  isMaderaPlusMode,
  isOffline,
  maderaPlusAvailableSpecies,
  role,
  specie,
  selectedAnnualPlan,
  projectSummary,
  prospectingPoints,
  visitList,
  onBiomasCO2Open,
  onContactRSC,
  onExportClick,
  onGetExanteProject,
  onGetExpostProject,
  onMaderaPlusFormOpen,
  onMaderaPlusEnd,
  onNotificationClick,
  onShareClick,
  onInterventionAdd,
  onInterventionSelect,
  onVisitAdd,
  onVisitDelete
}) => {

  const TABS = {
    DATA: 'data',
    STOCK: 'stock',
    MADERAPLUS: 'maderaPlus',
    VISITS: 'interventionZoneVisits',
    INTERVENTIONS: 'interventions'
  };
  
  const {t} = useTranslation();
  const {id, area, age, cuartel, interventions, elastic_module, refcat, species, stratum, ua, uo} = interventionZone;
  
  const formattedArea = area ? parseFloat(area.toFixed(2)) : 0;
  const formattedElasticModule = (interventionZone && !!interventionZone.length && elastic_module) 
    ? elastic_module : [];
  const formattedSpecies = species && species.length !== 0 ? species : [];
  
  const speciesIncludedInBiomasCo2 = SPECIES_INCLUDED_BIOMASCO2.map(specie => specie.bbddId);
  
  const hasBiomasCo2Species = plantedSpecies(formattedSpecies)
    .filter(specie => speciesIncludedInBiomasCo2.includes(specie.id))
    .length > 0;
  
  const howManyBiomasCo2Species = plantedSpecies(formattedSpecies)
    .filter(specie => speciesIncludedInBiomasCo2.includes(specie.id))
    .length;

  
  const handleExportClick = () => onExportClick();
  const handleNotificationClick = () => onNotificationClick();
  const handleShareClick = () => onShareClick();
  const [tabValue, setTabValue] = useState('data');
  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleMaderaPlusFormOpen = () => onMaderaPlusFormOpen();

  const showVisitsMenu = useMemo(() => tabValue === TABS.VISITS, [tabValue]);

  const [modalRSC, setModalRSC] = useState(false);

  const headerInterventionZoneMenu = () => <MenuHeader
    uo={uo}
    ua={ua}
    cuartel={cuartel}
    onNotificationClick={handleNotificationClick}
    onExportClick={handleExportClick}
    onShareClick={handleShareClick}
  />;

  const DataPanel = () => <MenuData age={age} area={formattedArea} refcat={refcat} stratum={stratum}/>;
  const RSCButton = () => <Button variant='contained' color='secondary' startIcon={<EmailIcon />} sx={{mt: 2, minWidth: '150px'}} onClick={() => setModalRSC(true)}>{t('CONTACTAR CON RSC')}</Button>;

  const InterventionList = () => <MenuInterventionsList
    interventions={interventions}
    isOffline={isOffline}
    role={role}
    selectedAnnualPlan={selectedAnnualPlan}
    onInterventionSelect={onInterventionSelect}
    onInterventionAdd={onInterventionAdd}
  />;

  const MaderaPlusResultsPanel = () => <MaderaPlusResults
    maderaPlusAvailableSpecies={maderaPlusAvailableSpecies}
    onMaderaPlusFormOpen={handleMaderaPlusFormOpen}
    elastic_module={formattedElasticModule}
  />;

  const StockPanels = () => <MenuExistences species={species}/>;

  const visitInterventionZoneList = useMemo(() => visitList?.filter(visit => visit.intervention_zone === id) || [], [visitList, id]);

  const InterventionZoneVisitMenu = () => <VisitMenu
    disableAddButton={visitInterventionZoneList.length > 0}
    isOffline={isOffline}
    isVisitsShown={showVisitsMenu}
    showAddButton={true}
    visitList={visitInterventionZoneList}
    onVisitAdd={onVisitAdd}
    onVisitDelete={onVisitDelete}
  />;

  const handleContact = () => {
    setModalRSC(false);
    onContactRSC(id);
  };

  const tabContent = () => {
    switch (tabValue) {
    case TABS.DATA:
      return <TabContent value={tabValue} sx={tabIntervention}>
        <DataPanel/>
        <RSCButton/>
      </TabContent>;
    case TABS.STOCK:
      return <TabContent value={tabValue} sx={tabIntervention}>
        <StockPanels/>
      </TabContent>;
    case TABS.MADERAPLUS:
      return <TabContent value={tabValue} sx={tabIntervention}>
        {
          !isMaderaPlusMode && <MaderaPlusResultsPanel/>
        }
        {
          isMaderaPlusMode && <MaderaPluspProspectionPointsCalculation
            prospectingPoints={prospectingPoints}
            specie={specie}
            onMaderaPlusEnd={onMaderaPlusEnd}
          />
        }
      </TabContent>;
    case TABS.INTERVENTIONS:
      return <TabContent value={tabValue} sx={tabIntervention}>
        <InterventionList/>
      </TabContent>;
    case TABS.VISITS:
      return <TabContent value={tabValue} sx={tabIntervention}>
        <InterventionZoneVisitMenu/>
      </TabContent>;
    }
  };

  return <div id='intervention-zone-detail'>
    <Hidden lgUp>{/*MOBILE*/}
      <Stack sx={{position: 'relative', height: '100%'}}>
        {headerInterventionZoneMenu()}
        <TabContext value={tabValue} variant='fullWidth' sx={{position: 'relative', height: '100%'}}>
          <Box mb={1}>
            <Tabs onChange={handleTabChange} value={tabValue} variant='scrollable' scrollButtons='auto' sx={{mb: 1}}>
              <Tab label={t('Datos')} value={TABS.DATA} sx={layerTab}/>
              <Tab label={t('stock')} value={TABS.STOCK} sx={layerTab}/>
              <Tab label={t('maderaPlusModule')} value={TABS.MADERAPLUS} sx={layerTab}/>
              <Tab label={t('Intervenciones')} value={TABS.INTERVENTIONS} sx={layerTab}/>
              <Tab label={t('Visitas')} value={TABS.VISITS} sx={layerTab}/>
            </Tabs>
            {tabContent()}
          </Box>
        </TabContext>
      </Stack>
    </Hidden>
    <Hidden lgDown>{/*DESKTOP*/}
      {headerInterventionZoneMenu()}
      <DataPanel/>
      <RSCButton/>
      <DropDown title={t('stock')} hasData={!!species?.length}>
        <StockPanels/>
      </DropDown>
      <DropDown title={t('Intervenciones')} id='intervention-zone-detail-intervenciones' hasData={!!interventions?.length}>
        <InterventionList/>
      </DropDown>
      <InterventionZoneVisitMenu/>
      {
        hasViewPermissions(VIEW_PERMISSIONS.BIOMAS_MODULE, role) && SHOW_BIOMAS_MODULE && 
        <DropDown title={t('BiomaisCO2')} hasData>
          {
            hasBiomasCo2Species
              ? howManyBiomasCo2Species === 1
                ? <BiomasCO2Menu
                  interventionZoneId={id}
                  isOffline={isOffline}
                  projectSummary={projectSummary}
                  onBiomasCO2Open={onBiomasCO2Open}
                  onGetExpostProject={onGetExpostProject}
                  onGetExanteProject={onGetExanteProject}
                />
                : <>
                  <Typography component='span'>{t('Para calcular el CO')}<sub>2</sub></Typography>
                  <Typography component='span'>{t(' de varias especies ponte en contacto con maderaplus@maderaplus.es')}</Typography>
                </>
              : <Typography component='span'>{t('No hay especies plantadas sobre las que hacer el cálculo')}</Typography>
          }
        </DropDown>
      }
      <DropDown title={t('maderaPlusModule').toUpperCase()} hasData={!!formattedSpecies?.length}>
        <MaderaPlusResultsPanel/>
      </DropDown>
    </Hidden>
    {
      modalRSC &&
      <DialogPanel onClose={() => setModalRSC(false)} isOpen={modalRSC} label={t('CONTACTAR CON RSC')} fullWidth={true}>
        <Typography gutterBottom>{t('¿Estás seguro de querer contactar con RSC?')}</Typography>
        <Typography gutterBottom>{t('Se enviará un mail manifestando tu interés en la Unidad de Actuación')}{ua}</Typography>
        <Box display='flex' flexDirection='row' justifyContent='flex-end' mt={1} mr={1.2} sx={{gap: 2}}>
          <Button color="secondary" onClick={() => setModalRSC(false)}>{t('cancelar')}</Button>
          <Button color="secondary" variant='contained' onClick={handleContact}>{t('aceptar')}</Button>
        </Box>
      </DialogPanel>
    }
  </div>;
};

export default InterventionZoneMenu;

InterventionZoneMenu.propTypes = {
  interventionZone: interventionZone,
  isMaderaPlusMode: PropTypes.bool,
  isOffline: PropTypes.bool,
  maderaPlusAvailableSpecies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    existences: PropTypes.shape({
      age: PropTypes.number,
      treeFeet: PropTypes.number,
      height: PropTypes.number,
      basometricArea: PropTypes.number,
      volumeWithBark: PropTypes.number,
      volumeWithoutBark: PropTypes.number,
      biomass: PropTypes.number,
    })
  })),
  projectSummary: projectSummary,
  prospectingPoints: prospectingPoints,
  role: PropTypes.oneOf(Object.values(ROLES_LABEL)),
  selectedAnnualPlan: annualPlan,
  specie: PropTypes.shape(existences),
  visitList: PropTypes.arrayOf(visit),
  onBiomasCO2Open: PropTypes.func,
  onContactRSC: PropTypes.func,
  onExportClick: PropTypes.func.isRequired,
  onGetExanteProject: PropTypes.func,
  onGetExpostProject: PropTypes.func,
  onMaderaPlusFormOpen: PropTypes.func,
  onMaderaPlusEnd: PropTypes.func,
  onNotificationClick: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  onInterventionAdd: PropTypes.func.isRequired,
  onInterventionSelect: PropTypes.func.isRequired,
  onVisitAdd: PropTypes.func.isRequired,
  onVisitDelete: PropTypes.func.isRequired
};
